import { useCallback, useEffect, useState } from "react";
import { expense_filter_Data } from "../api/expense_filter";
import { createExpense, deleteExpense, updateExpense } from "../api/expense_API";
import { toISODate } from "../../utils/dateUtils";

const getInitialRange = () => {
  const today = new Date();
  return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
};

export function useExpenseData({ toast }) {
  const [range, setRange] = useState(getInitialRange);
  const [expenseData, setExpenseData] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);

  const loadExpenses = useCallback(async () => {
    if (!range.from || !range.to) return;

    const res = await expense_filter_Data(
      { start: range.from, end: range.to },
      toast
    );

    const selectedMonth = `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, "0")}`;
    const list =
      res?.data?.filter((r) => String(r.date || "").startsWith(selectedMonth)).map((r) => ({
        id: r.expense_uuid,
        name: r.expense_name,
        detail: r.expense_detail,
        paymentMethod: r.payment_method || r.payment || "",
        isSalary: Boolean(r.work_day),
        amount: Number(r.amount),
        date: new Date(`${r.date}T00:00:00`),
      })) || [];

    setExpenseData(list);
    setTotalExpense(list.reduce((a, c) => a + c.amount, 0));
  }, [range, toast]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const saveExpenseItems = async (expenseItems) => {
    for (const item of expenseItems) {
      const result = await createExpense(
        {
          date: toISODate(item.date),
          expense_name: item.name,
          expense_detail: item.detail,
          payment_method: item.paymentMethod,
          amount: Number(item.amount),
        },
        toast
      );
      if (!result?.success) throw new Error("지출 저장에 실패했습니다.");
    }
    await loadExpenses();
  };

  const updateExpenseItem = async (item) => {
    const result = await updateExpense({
      expense_uuid: item.id,
      start_date: toISODate(range.from),
      end_date: toISODate(range.to),
      date: toISODate(item.date),
      expense_name: item.name,
      expense_detail: item.detail,
      payment_method: item.paymentMethod,
      amount: Number(item.amount),
    }, toast);
    if (!result?.success) throw new Error("지출 수정에 실패했습니다.");
    await loadExpenses();
  };

  const deleteExpenseItem = async (item) => {
    const result = await deleteExpense({
      expense_uuid: item.id,
      start_date: toISODate(range.from),
      end_date: toISODate(range.to),
    }, toast);
    if (!result?.success) {
      toast?.({ title: "지출 삭제 실패", status: "error", duration: 3000, isClosable: true });
      return false;
    }
    await loadExpenses();
    toast?.({ title: "지출 내역을 삭제했습니다.", status: "success", duration: 2500, isClosable: true });
    return true;
  };

  return {
    range,
    setRange,
    expenseData,
    setExpenseData,
    totalExpense,
    saveExpenseItems,
    updateExpenseItem,
    deleteExpenseItem,
  };
}
